// No require here: the import page bundles this module to check a delivery before uploading it,
// and webpack provides neither fs nor path. Node reads it through sources.js.

const DELIVERY_FOLDERS = ['images', 'image', 'zones', 'zone', 'pdv', 'logo', 'logos'];
const PLAN_FOLDERS = ['jpg', 'jpeg', 'pdf', 'pdfs'];

// Lowercase without diacritics, so "Zones" and "Logos" are found whatever the delivery uses.
function foldName(value) {
    return String(value)
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase();
}

// Frame order drives the per-floor split, so it must not depend on the filesystem.
function naturalCompare(first, second) {
    return String(first).localeCompare(String(second), 'en', { numeric: true, sensitivity: 'base' });
}

// The archive usually wraps the delivery in one folder ("PDV V2/jpg/A001.jpg"), with a __MACOSX
// sibling on macOS: look through it, unless that folder is itself one of the delivery's own
// folders. Loose files beside it do not count. archiveKind and the extraction both ask this, and
// two answers would send a delivery down a pipeline that is not its own.
function wrapperFolderOf(rootDirectories) {
    const candidates = [...new Set(rootDirectories)].filter(name => foldName(name) !== '__macosx');

    if (candidates.length !== 1) {
        return null;
    }

    const folded = foldName(candidates[0]);

    return DELIVERY_FOLDERS.includes(folded) || PLAN_FOLDERS.includes(folded) ? null : candidates[0];
}

// The first-level folders of a delivery as the archive names them, read through its wrapping
// folder, __MACOSX left out. Loose files at that level are not folders and are left out too.
function deliveryFolders(entryNames) {
    const entries = entryNames
        .map(name => {
            const value = String(name).replace(/\\/g, '/').replace(/^\/+/, '');

            return { isDirectory: value.endsWith('/'), segments: value.split('/').filter(Boolean) };
        })
        .filter(entry => entry.segments.length > 0 && foldName(entry.segments[0]) !== '__macosx');

    const wrapped = wrapperFolderOf(entries
        .filter(entry => entry.isDirectory || entry.segments.length > 1)
        .map(entry => entry.segments[0])) !== null;

    const folders = entries
        .map(entry => {
            const segments = wrapped ? entry.segments.slice(1) : entry.segments;

            return entry.isDirectory ? segments : segments.slice(0, -1);
        })
        .filter(segments => segments.length > 0)
        .map(segments => segments[0]);

    return [...new Set(folders)].sort(naturalCompare);
}

// What kind of delivery an archive holds, read from its entry names alone so the caller can
// route the pipeline before paying to extract 650 MB. Deliberately narrow: it answers "are
// there frames in here?", nothing more. prepare_sources stays the judge of what a delivery
// actually contains, and fails as it always has when one does not keep its promises.
function archiveKind(entryNames) {
    const heads = new Set(deliveryFolders(entryNames).map(foldName));

    if (heads.has('images') || heads.has('image')) {
        return 'full';
    }

    if (heads.has('pdv') || [...heads].some(head => PLAN_FOLDERS.includes(head))) {
        return 'plans';
    }

    return 'unknown';
}

// Non-breaking spaces inside the guillemets and before the colons: a line break there leaves a
// lone "»" at the start of the next line under the form field.
function unknownDeliveryMessage(folders) {
    const found = folders.length > 0
        ? `Dossiers trouvés : ${folders.map(folder => `« ${folder} »`).join(', ')}.`
        : 'Aucun dossier trouvé.';

    return "Structure de livraison non reconnue. L'archive doit contenir soit un dossier « Images » et un dossier « Zones » (livraison complète), soit uniquement des plans : un dossier « PDV », ou des dossiers « jpg » et/ou « pdf ». Ces dossiers peuvent être directement dans le zip, ou regroupés dans un seul dossier. "
        + found;
}

module.exports = {
    DELIVERY_FOLDERS,
    PLAN_FOLDERS,
    archiveKind,
    deliveryFolders,
    foldName,
    naturalCompare,
    unknownDeliveryMessage,
    wrapperFolderOf,
};
